// components/PartReplacementDetailsFilter.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import DealerDropdown from '../../../../components/common/DealerDropdown';
import ChassisDropdown from '../../../../components/common/ChassisDropdown';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetAllModelDropdownListQuery, useGetLookUpDropdownListQuery } from '../../../../api';
import SapDealerCountryDropdown from '../../../../components/common/SapDealerCountryDropdown';

const statusOptions = [
    { value: 10, key: 'drafts' },
    { value: 15, key: 'returned' },
    { value: 20, key: 'waiting' },
    { value: 30, key: 'accepted' },
    { value: 40, key: 'rejected' }
];

export default function PartReplacementDetailsFilter({ initialChassisNumber, onApply, onReset, isDealer }) {
    const { th, getCurrentLanguageId } = useLanguage();

    const [filterOpen, setFilterOpen] = useState(true);

    const [filters, setFilters] = useState({
        chassisNumber: initialChassisNumber || null,
        dealer: null,
        claimDate: null,
        failureDate: null,
        failureStartDate: null,
        failureEndDate: null,
        status: null,
        models: [],
        country: null,
        isScrap: null,
        warrantyType: null
    });

    const [modelSearchText, setModelSearchText] = useState('');

    const toggleOpen = () => setFilterOpen((p) => !p);

    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            chassisNumber: initialChassisNumber || null
        }));
    }, [initialChassisNumber]);

    const setField = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const { data: modelData = [], isLoading: isLoadingModels } = useGetAllModelDropdownListQuery({
        brandName: '',
        productGroup: '',
        modelName: modelSearchText || ''
    });

    const modelOptions = useMemo(
        () =>
            (modelData || []).map((model) => ({
                id: model.id,
                name: model.model,
                value: model.model
            })),
        [modelData]
    );

    const { data: warrantyTypeList = [], isLoading: isLoadingWarrantyTypes } = useGetLookUpDropdownListQuery({
        GroupCode: 'ITEM_LABOR_WARRANTY_TYPE',
        LanguageId: getCurrentLanguageId()
    });

    const warrantyTypeOptions = useMemo(() => {
        const raw = warrantyTypeList?.data || warrantyTypeList || [];
        return raw.map((w) => ({
            value: parseInt(w.code, 10),
            label: w.translatedText
        }));
    }, [warrantyTypeList]);

    const isScrapOptions = useMemo(
        () => [
            { value: 1, label: th('warrantyClaims.detail.partDialog.keepPart') },
            { value: 2, label: th('warrantyClaims.detail.partDialog.scrapPart') }
        ],
        [th]
    );

    const apply = () => {
        var countrycode = filters.country?.code;
        console.log('filter:', filters);
        const modelsPayload =
            Array.isArray(filters.models) && filters.models.length
                ? filters.models
                      .map((m) => m?.value || m?.name || m)
                      .map((s) => String(s).trim())
                      .filter(Boolean)
                : undefined;

        onApply?.({
            chassisNumber: filters.chassisNumber || undefined,
            dealerId: isDealer ? undefined : filters.dealer?.id ?? undefined,
            //  dealerId: filters.dealer?.id ?? undefined,
            claimDate: filters.claimDate ?? undefined,
            failureDate: filters.failureDate ?? undefined,
            failureStartDate: filters.failureStartDate ?? undefined,
            failureEndDate: filters.failureEndDate ?? undefined,
            status: filters.status ?? undefined,
            models: modelsPayload,
            countryCode: filters.country?.code ?? undefined,
            isScrap: filters.isScrap ?? undefined,
            warrantyType: filters.warrantyType ?? undefined
        });
    };

    const reset = () => {
        setFilters({
            chassisNumber: initialChassisNumber || null,
            dealer: null,
            claimDate: null,
            failureDate: null,
            failureStartDate: null,
            failureEndDate: null,
            status: null,
            models: [],
            country: null,
            isScrap: null,
            warrantyType: null
        });
        onReset?.();
    };

    return (
        <div className="generic-table-filter mb-3">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button icon="pi pi-filter" label={th('warrantyReports.filter.advancedFilter')} onClick={toggleOpen} outlined={!filterOpen} />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('warrantyReports.filter.advancedFilters')}</div>

                    <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.columns.ChassisNo')}</label>
                                <ChassisDropdown value={filters.chassisNumber} onChange={(val) => setField('chassisNumber', val)} className="w-full" />
                            </div>
                        </div>
                        {!isDealer && (
                            <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                <div className="field mb-2 p-1">
                                    <label className="block mb-1 text-sm">{th('warrantyReports.filter.dealer')}</label>
                                    <DealerDropdown value={filters.dealer} onChange={(val) => setField('dealer', val)} className="w-full" rowCount={20} placeholder={th('warrantyReports.filter.dealerPlaceholder')} />
                                </div>
                            </div>
                        )}
                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('common.status')}</label>
                                <Dropdown
                                    value={filters.status}
                                    options={statusOptions.map((s) => ({
                                        value: s.value,
                                        label: th(`warrantyClaims.tabs.${s.key}`)
                                    }))}
                                    onChange={(e) => setField('status', e.value)}
                                    placeholder={th('warrantyReports.filter.selectPlaceholder')}
                                    showClear
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.details.columns.ClaimDate')}</label>
                                <Calendar value={filters.claimDate} onChange={(e) => setField('claimDate', e.value)} dateFormat="dd/mm/yy" showIcon className="w-full" placeholder={th('warrantyReports.filter.selectPlaceholder')} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.details.columns.FailureDate')}</label>
                                <Calendar value={filters.failureDate} onChange={(e) => setField('failureDate', e.value)} dateFormat="dd/mm/yy" showIcon className="w-full" placeholder={th('warrantyReports.filter.selectPlaceholder')} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.details.filter.failureStartDate')}</label>
                                <Calendar value={filters.failureStartDate} onChange={(e) => setField('failureStartDate', e.value)} dateFormat="dd/mm/yy" showIcon className="w-full" placeholder={th('warrantyReports.filter.selectPlaceholder')} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.details.filter.failureEndDate')}</label>
                                <Calendar value={filters.failureEndDate} onChange={(e) => setField('failureEndDate', e.value)} dateFormat="dd/mm/yy" showIcon className="w-full" placeholder={th('warrantyReports.filter.selectPlaceholder')} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.details.filter.country')}</label>
                                <SapDealerCountryDropdown value={filters.country} onChange={(val) => setField('country', val)} className="w-full" placeholder={th('warrantyReports.filter.selectPlaceholder')} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyClaims.detail.partDialog.isScrap')}</label>
                                <Dropdown value={filters.isScrap} options={isScrapOptions} onChange={(e) => setField('isScrap', e.value)} placeholder={th('warrantyReports.filter.selectPlaceholder')} showClear className="w-full" />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.details.filter.warrantyType')}</label>
                                <Dropdown
                                    value={filters.warrantyType}
                                    options={warrantyTypeOptions}
                                    onChange={(e) => setField('warrantyType', e.value)}
                                    placeholder={th('warrantyReports.filter.selectPlaceholder')}
                                    showClear
                                    className="w-full"
                                    loading={isLoadingWarrantyTypes}
                                />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('warrantyReports.details.filter.model')}</label>
                                <MultiSelect
                                    value={filters.models}
                                    options={modelOptions}
                                    onChange={(e) => setField('models', e.value)}
                                    onFilter={(e) => setModelSearchText(e.filter)}
                                    optionLabel="name"
                                    display="chip"
                                    filter
                                    className="w-full"
                                    placeholder={th('warrantyReports.details.filter.modelPlaceholder')}
                                    loading={isLoadingModels}
                                    virtualScrollerOptions={{ itemSize: 38 }}
                                    itemTemplate={(option) => (
                                        <div>
                                            <span style={{ fontWeight: 'bold' }}>{option.name}</span>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3">
                        <Button label={th('warrantyReports.filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={reset} />
                        <Button label={th('warrantyReports.filter.apply')} icon="pi pi-check" onClick={apply} />
                    </div>
                </div>
            )}
        </div>
    );
}
